import React, { FC } from 'react';
import { Button, Switch, Select, Tooltip, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import HelpArticleButton from 'jsUtilities/HelpArticleButton';
import styles from './index.module.css';
import GreenTick from '../../../views/integration_settings/images/ic-green-tick.svg';
import * as translations from './strings';
import { getLangKey } from 'appJS/models/Application';
import { UserSettings } from '..';

const strings = translations[getLangKey()];
const DUE_DATE_REMINDER_MINUTES = [0, 4320];

type Props = {
  authenticityToken: string;
  user: UserSettings;
  handleUpdateUser: (attributes: { [key: string]: any }) => void;
  saving: boolean;
  setSaving: (saving: boolean) => void;
  isGuest: boolean;
};

export const Notifications: FC<Props> = ({
  authenticityToken,
  user,
  handleUpdateUser,
  saving,
  setSaving,
  isGuest
}) => {
  const {
    schedule,
    newsletter,
    enableTaskUpdates,
    enableProjectUpdates,
    enableTaskCommentUpdates,
    enableUnassignedTaskCommentUpdates,
    enableNewTaskUpdates,
    enableNewTaskClientsOnly,
    enableTaskDoneUpdates,
    dueDateReminderMinutes
  } = user;

  const formHandler = () => {
    const formData = new FormData();
    formData.append('utf8', '✓');
    formData.append('_method', 'patch');
    formData.append('user[notify_frequency]', String(schedule ?? 0));
    formData.append('user[newsletter]', String(newsletter ?? 0));
    formData.append('user[task_notify]', String(!!enableTaskUpdates));
    formData.append(
      'user[task_notify_comment]',
      String(!!enableTaskCommentUpdates)
    );
    formData.append(
      'user[task_notify_comment_unassigned]',
      String(!!enableUnassignedTaskCommentUpdates)
    );
    formData.append('authenticity_token', authenticityToken);
    formData.append('user[new_task_notify]', String(!!enableNewTaskUpdates));
    formData.append(
      'user[new_task_notify_clients_only]',
      String(!!(enableNewTaskUpdates && enableNewTaskClientsOnly))
    );
    formData.append('user[task_notify_done]', String(!!enableTaskDoneUpdates));
    formData.append(
      'user[enable_project_updates]',
      String(!!enableProjectUpdates)
    );
    if (!isGuest) {
      formData.append('user[due_date_reminder_minutes][]', '');
      if (dueDateReminderMinutes.length > 0) {
        DUE_DATE_REMINDER_MINUTES.forEach(minutes =>
          formData.append('user[due_date_reminder_minutes][]', String(minutes))
        );
      }
    }

    setSaving(true);

    fetch('/save_settings', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: { Accept: 'application/json' }
    })
      .then(response => {
        setSaving(false);

        if (response.status === 200) {
          message.success(strings.notificationSettingsSaved);
        } else {
          throw Error(response.statusText);
        }
      })
      .catch(error => message.error(strings.somethingWentWrong));
  };

  const handleProjectEnabled = (value: boolean) => {
    handleUpdateUser({ enableProjectUpdates: value, schedule: value ? 2 : 0 });
  };

  const handleNewTaskUpdatesChange = (checked: boolean) => {
    handleUpdateUser({
      enableNewTaskUpdates: checked,
      ...(checked ? {} : { enableNewTaskClientsOnly: false })
    });
  };

  return (
    <div className={styles.container}>
      <h2>{strings.notificationPreferences}</h2>

      {!isGuest && (
        <section className={styles.section}>
          <header className={styles.header}>
            <h3>{strings.newTasksCreated}</h3>
            <Switch
              checked={!!enableNewTaskUpdates}
              onChange={handleNewTaskUpdatesChange}
            />
          </header>
          <p>{strings.receiveEmail}</p>
          <ul className={styles.list}>
            <li>
              <Switch
                size="small"
                checked={!!enableNewTaskClientsOnly}
                disabled={!enableNewTaskUpdates}
                onChange={checked =>
                  handleUpdateUser({ enableNewTaskClientsOnly: checked })
                }
              />
              &nbsp;{strings.onlyNotifyClients}
            </li>
          </ul>
        </section>
      )}

      <section className={styles.section}>
        <header className={styles.header}>
          <h3>{strings.taskUpdates}</h3>
          <Switch
            checked={enableTaskUpdates}
            onChange={checked =>
              handleUpdateUser({ enableTaskUpdates: checked })
            }
          />
        </header>
        <p>
          {strings.receiveEmails} <strong>{strings.instantly}</strong>{' '}
          {strings.whenThere}
        </p>
        <ul className={styles.list}>
          <li>
            <GreenTick className={styles.greenTickIcon} />
            &nbsp;&nbsp;{strings.taskAssignedToYou}
          </li>
          <li>
            <GreenTick className={styles.greenTickIcon} />
            &nbsp;&nbsp;{strings.commentOnATaskReported}
          </li>
          <li>
            <GreenTick className={styles.greenTickIcon} />
            &nbsp;&nbsp;{strings.commentOnATaskAssigned}
          </li>
          <li>
            <Switch
              size="small"
              checked={enableTaskCommentUpdates}
              onChange={checked =>
                handleUpdateUser({ enableTaskCommentUpdates: checked })
              }
            />
            &nbsp;{strings.commentOnATask}
          </li>
          <li>
            <Switch
              size="small"
              checked={enableUnassignedTaskCommentUpdates}
              onChange={checked =>
                handleUpdateUser({
                  enableUnassignedTaskCommentUpdates: checked
                })
              }
            />
            &nbsp;{strings.commentOnAnUnassignedTask}
          </li>
          {isGuest && (
            <li>
              <Switch
                size="small"
                checked={enableTaskDoneUpdates}
                onChange={checked =>
                  handleUpdateUser({
                    enableTaskDoneUpdates: checked
                  })
                }
              />
              &nbsp;{strings.taskFollowingCompleted}
            </li>
          )}
        </ul>
      </section>

      {!isGuest && (
        <section className={styles.section}>
          <header className={styles.header}>
            <h3>
              {strings.dueDateReminders}
              <Tooltip title={strings.dueDateRemindersInfo}>
                <InfoCircleOutlined className={styles.infoIcon} />
              </Tooltip>
            </h3>
            <Switch
              checked={dueDateReminderMinutes.length > 0}
              onChange={checked =>
                handleUpdateUser({
                  dueDateReminderMinutes: checked
                    ? DUE_DATE_REMINDER_MINUTES
                    : []
                })
              }
            />
          </header>
          <p>{strings.dueDateRemindersDescription}</p>
        </section>
      )}

      <section className={styles.section}>
        <header className={styles.header}>
          <h3>{strings.projectUpdates}</h3>
          <Switch
            checked={enableProjectUpdates}
            onChange={handleProjectEnabled}
          />
        </header>
        <p>
          {strings.aSummary}({strings.seeAn}
          <HelpArticleButton articleId="33888" style={{ color: '#3f71e0' }}>
            {strings.exampleHere}
          </HelpArticleButton>
          ).
        </p>
        <p>{strings.updateWillInclude}</p>
        <ul className={styles.list}>
          <li>
            <GreenTick className={styles.greenTickIcon} />
            &nbsp;&nbsp;{strings.newTasksCreated}
          </li>
          <li>
            <GreenTick className={styles.greenTickIcon} />
            &nbsp;&nbsp;{strings.comments}
          </li>
          <li>
            <GreenTick className={styles.greenTickIcon} />
            &nbsp;&nbsp;{strings.tasksMovedToDone}
          </li>
          <li>
            <GreenTick className={styles.greenTickIcon} />
            &nbsp;&nbsp;{strings.tasksClosed}
          </li>
        </ul>

        <div className={styles.scheduleContainer}>
          <span className={styles.scheduleLabel}>
            {strings.sendProjectUpdatesToMe}
          </span>
          <Select
            disabled={!enableProjectUpdates}
            value={schedule}
            style={{ width: 120 }}
            onChange={value => handleUpdateUser({ schedule: value })}
            getPopupContainer={() => {
              let container = document.getElementById('settings_container');
              if (!container) {
                container = document.createElement('div');
                container.id = 'settings_container';
                document.body.appendChild(container);
              }
              return container;
            }}
          >
            <Select.Option value={0}>{strings.never}</Select.Option>
            <Select.Option value={2}>{strings.hourly}</Select.Option>
            <Select.Option value={4}>{strings.daily}</Select.Option>
            <Select.Option value={5}>{strings.weekly}</Select.Option>
          </Select>
        </div>
      </section>

      <section className={styles.section}>
        <header className={styles.header}>
          <h3>{strings.subscribeToBugherd}</h3>
          <Switch
            checked={!!newsletter}
            onChange={checked =>
              handleUpdateUser({ newsletter: checked ? 1 : 0 })
            }
          />
        </header>
        <p>{strings.subscribeDescription}</p>
      </section>

      <Button type="primary" onClick={formHandler} loading={saving}>
        {strings.save}
      </Button>
    </div>
  );
};
